import React, { useCallback, useContext, useEffect, useMemo } from 'react'

import { useSelector } from 'react-redux'

import Loading from '../../../HOC/Loading'
import Analytics from '../../../utils/analytics'
import { selectUser } from '../../../reducers/UserReducer'
import { getSectorPresets } from '../../../utils/api/sector'
import { getInstitutionSectors, getInstitutionShiftPresets } from '../../../utils/api/institution'
import SectorShiftPresetsManager from './SectorShiftPresetsManager'
import { InstitutionOptions } from '../../../utils/constants'
import { ColorsPalette } from '../../../config/colors'
import { isNurse, isObserver } from '../../../utils/roles'
import useRequest from '../../../hooks/useRequest'
import { firstOrDefault, isObject } from '../../../utils'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { InstitutionContext } from '../../../Providers/InstitutionProvider'

import '../../../assets/shifts-presets-manager.scss'

const ShiftPresetsManager = ({ sector }) => {
  const { options } = useContext(InstitutionContext)

  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const institution = useMemo(() => firstOrDefault(user.institutions, undefined), [user.institutions])

  const sectors = useRequest({
    request: getInstitutionSectors,
    parameters: institution,
    enabled: !isObject(sector)
  })

  const institutionShiftPresets = useRequest({
    request: getInstitutionShiftPresets,
    parameters: institution,
    enabled: !isObject(sector) && !sectors.loading
  })

  const sectorPresets = useRequest({
    request: getSectorPresets,
    parameters: sector,
    enabled: isObject(sector),
    select: useCallback(data => ({ [sector.id]: data }), [])
  })

  const loading = useMemo(() => isObject(sector) ? sectorPresets.loading : (sectors.loading || institutionShiftPresets.loading), [sectorPresets.loading, sectors.loading, institutionShiftPresets.loading])

  const disabled = useMemo(() => {
    return !!((isNurse(user) && options[InstitutionOptions.BLOCK_SCHEDULE_CODE_EDITION]) || isObserver(user))
  }, [options, user])

  useEffect(() => {
    Analytics.pageView('/shifts-presets-manager')
  }, [])

  const title = useMemo(() => disabled
    ? (
      <div>
        <h3>{t('views.shifts_manager.alt_title')}</h3>
        <p style={{ color: ColorsPalette.GREY }}>{t('unauthorized.edition.code.message')}</p>
      </div>
    ) : null
  , [disabled])

  const displayedSectors = useMemo(() => isObject(sector) ? [sector] : sectors.data, [sector, sectors.data])
  const displayedPresets = useMemo(() => isObject(sector) ? sectorPresets.data : institutionShiftPresets.data, [sectorPresets.data, institutionShiftPresets.data])

  return (
    <div>
      <Loading loading={loading} size='6x'>
        {title}
        <ul className='presets-list'>
          {displayedSectors.map(s => (
            <li key={`shift-preset-item-${s.id}`}>
              <SectorShiftPresetsManager disabled={disabled} shiftPresets={displayedPresets} sector={s} />
            </li>
          ))}
        </ul>
      </Loading>
    </div>
  )
}

export default ShiftPresetsManager
